/**
 * metaPixel — SPA PageView dispatch helpers for the Meta Pixel install.
 *
 * Feb 2026 dispatch — Meta Pixel PageView tracking. The inline
 * pixel loader lives in `frontend/public/index.html` and is gated
 * on `REACT_APP_META_PIXEL_ID` being non-empty at build time.
 * When the env var is empty (dev, tests, dark-launch envs) the
 * loader is a no-op and `window.fbq` is never defined — every
 * function in this module short-circuits on that condition.
 *
 * Exclusions (both apply to `trackPageView`):
 *   1. `/admin/*` routes — internal ops surface, not part of the
 *      conversion funnel Meta needs to see.
 *   2. Impersonation sessions — a support engineer inside a
 *      customer's account MUST NOT emit that customer's route
 *      history to a third-party ad network. Cite:
 *      `AdminModeBar.jsx:55` — `isImpersonating = !!user?.is_impersonating`.
 *
 * The initial `PageView` fires from the SPA hook on mount (not
 * from the inline HTML snippet) so the exclusion logic runs on
 * the FIRST route too — otherwise a support engineer opening
 * `/admin/accounts` fresh would leak that URL on load.
 *
 * Consent gating (Feb 2026 Lane E — SHIPS DARK):
 *   Feature-flagged via `REACT_APP_CONSENT_GATING_ENABLED`.
 *   Default OFF — pixel loads via the inline snippet in
 *   `index.html` as today. When flag is ON, the inline snippet
 *   short-circuits until the user grants consent via
 *   `<ConsentBanner />`. `maybeInitPixel()` below handles the
 *   post-consent dynamic init so users don't need to refresh.
 *   See `lib/consent.js` module header for the full scope +
 *   the "first-party beacon is NOT gated" rationale.
 */
import { shouldPixelLoad } from "@/lib/consent";

/** True iff the Meta Pixel loader initialized (env-set + snippet ran). */
export function isPixelEnabled() {
  return typeof window !== "undefined" && typeof window.fbq === "function";
}

/**
 * True iff the pathname / session should be excluded from PageView.
 * Kept pure so the unit tests can exercise the decision table
 * without mounting the pixel loader.
 *
 * @param {string} pathname — the router pathname, e.g. `/admin/accounts`.
 * @param {{ isImpersonating?: boolean }} opts
 */
export function shouldExcludePathForPixel(pathname, opts) {
  const isImpersonating = !!(opts && opts.isImpersonating);
  if (isImpersonating) return true;
  if (typeof pathname !== "string") return false;
  if (pathname.startsWith("/admin")) return true;
  return false;
}

/**
 * Fire a Meta Pixel PageView for the given route. No-op when the
 * pixel is disabled OR the path/session is excluded.
 *
 * @param {string} pathname
 * @param {{ isImpersonating?: boolean }} [opts]
 */
export function trackPageView(pathname, opts) {
  if (!isPixelEnabled()) return;
  if (shouldExcludePathForPixel(pathname, opts)) return;
  try {
    window.fbq("track", "PageView");
  } catch {
    // Defensive: never let a third-party tracker crash the app.
  }
}

/**
 * Dynamically load the Meta Pixel after page load. Called from
 * `<ConsentBanner />` when the user clicks Accept — so the user
 * doesn't need a page refresh to see accurate PageView tracking
 * from the moment of consent.
 *
 * Idempotent + safe:
 *   * No-op if `window.fbq` already exists (loader already ran).
 *   * No-op if `shouldPixelLoad()` returns false (defence-in-depth:
 *     never load without a clear "yes" from `lib/consent.js`).
 *   * No-op in SSR / non-browser environments.
 *
 * Uses the same loader IIFE as `index.html` for wire-parity so a
 * post-consent load looks identical to a build-time load from
 * Meta's side.
 */
export function maybeInitPixel() {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  if (typeof window.fbq === "function") return;
  if (!shouldPixelLoad()) return;
  const PIXEL_ID = "4398315853749426";
  try {
    /* eslint-disable */
    !function (f, b, e, v, n, t, s) {
      if (f.fbq) return;
      n = f.fbq = function () {
        n.callMethod
          ? n.callMethod.apply(n, arguments)
          : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = !0;
      n.version = "2.0";
      n.queue = [];
      t = b.createElement(e);
      t.async = !0;
      t.src = v;
      s = b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t, s);
    }(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
    /* eslint-enable */
    window.fbq("init", PIXEL_ID);
  } catch {
    // Never let a third-party loader crash the app.
  }
}
